import { isNumber, isDefined } from '#library/assertion.library.js';
const TWO_SECONDS = 2_000; // default time-out for requests, in milliseconds
const RE_TRAILING_CLOSURE = /\);?$/;
export const HTTP = {
    Ok: 200,
    PermRedirect: 301,
    TempRedirect: 302,
    BadRequest: 400,
    Unauthorised: 401,
    Forbidden: 403,
};
export const METHOD = {
    Head: 'HEAD',
    Get: 'GET',
    Put: 'PUT',
    Delete: 'DELETE',
    Post: 'POST',
};
export class HttpError extends Error {
    status;
    statusText;
    body;
    constructor(status, statusText, body) {
        super(`${status}: ${statusText}`);
        this.status = status;
        this.statusText = statusText;
        this.body = body;
        this.name = 'HttpError';
    }
}
/**
 * Internal helper: incrementally reads a response body, enforcing a cumulative maxBytes limit.
 */
const readBoundedBody = async (res, maxBytes) => {
    const contentLength = res.headers.get('Content-Length') || res.headers.get('content-length');
    if (contentLength) {
        const bytes = parseInt(contentLength, 10);
        if (isNumber(bytes) && bytes > maxBytes) {
            try {
                await res.body?.cancel();
            }
            catch { }
            throw new HttpError(413, `Content-Length (${bytes}) exceeds limit (${maxBytes} bytes)`, null);
        }
    }
    if (!res.body) {
        const rawText = await res.text();
        if (rawText.length > maxBytes)
            throw new HttpError(413, `Payload length (${rawText.length}) exceeds limit (${maxBytes} bytes)`, null);
        return rawText;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let totalBytes = 0;
    let rawText = '';
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            if (value) {
                totalBytes += value.byteLength;
                if (totalBytes > maxBytes) {
                    try {
                        await reader.cancel();
                    }
                    catch { }
                    throw new HttpError(413, `Streamed payload exceeded limit (${maxBytes} bytes)`, null);
                }
                rawText += decoder.decode(value, { stream: true });
            }
        }
        rawText += decoder.decode();
        return rawText;
    }
    finally {
        try {
            reader.releaseLock();
        }
        catch { }
    }
};
/**
 * Performs an HTTP fetch request with built-in timeout, JSON parsing, prefix handling, and bounded stream reading.
 * Automatically throws an `HttpError` if the response is not `ok` or exceeds `maxBytes`.
 *
 * @param url - The resource URL to fetch
 * @param init - Optional RequestInit configuration
 * @param config - Optional configuration including timeout, prefix stripping, and maxBytes limit
 * @returns A promise resolving to the parsed response body
 * @example
 * ```ts
 * const data = await fetchRequest<MyData>('https://api.example.com', {}, { maxBytes: 512 * 1024 });
 * ```
 */
export const fetchRequest = (url, init = {}, config = {}) => {
    const signallingInit = {
        ...init,
        signal: init.signal
            ? AbortSignal.any([init.signal, AbortSignal.timeout(config.timeout ?? TWO_SECONDS)])
            : AbortSignal.timeout(config.timeout ?? TWO_SECONDS)
    };
    return fetch(url, signallingInit) // caller will handle the 'catch' if error
        .then(async (res) => {
        if (res.ok) {
            const contentType = res.headers.get('Content-Type') || '';
            const isJson = contentType.includes('application/json');
            if (isDefined(config.maxBytes)) {
                const rawText = await readBoundedBody(res, config.maxBytes);
                if (config.prefix) {
                    const json = rawText.startsWith(config.prefix)
                        ? rawText.substring(config.prefix.length).replace(RE_TRAILING_CLOSURE, '')
                        : rawText;
                    return JSON.parse(json);
                }
                return (isJson ? JSON.parse(rawText) : rawText);
            }
            if (config.prefix) {
                const rawPrefixText = await res.text(); // read raw text first
                const json = rawPrefixText.startsWith(config.prefix) // if it starts with the specified prefix
                    ? rawPrefixText.substring(config.prefix.length).replace(RE_TRAILING_CLOSURE, '') // then strip the prefix AND any trailing closure
                    : rawPrefixText;
                return JSON.parse(json); // parse the unwrapped string
            }
            return await (isJson
                ? res.json() // default JSON parsing
                : res.text());
        }
        let errorBody = null;
        try {
            const errorText = await res.text();
            try {
                errorBody = JSON.parse(errorText);
            }
            catch {
                errorBody = errorText;
            }
        }
        catch { }
        throw new HttpError(res.status, res.statusText, errorBody); // fetch not successful
    });
};
/**
 * Performs an HTTP HEAD request to retrieve response headers without the body.
 * Useful for verifying URL existence or checking metadata.
 *
 * @param url - The resource URL to check
 * @returns A promise resolving to the response status and headers
 * @example
 * ```ts
 * const { status } = await fetchHead('https://example.com');
 * ```
 */
export const fetchHead = (url) => {
    const signal = AbortSignal.timeout(TWO_SECONDS);
    const init = { method: METHOD.Head, signal }; // only interested in verifying that url responds
    return fetch(url, init) // caller will handle the 'catch' if error
        .then(({ ok, status, statusText, headers }) => {
        if (ok || status === HTTP.Forbidden) // forbidden, but at least we know url responds
            return { status, headers };
        throw new HttpError(status, statusText, null); // fetch not successful
    });
};
